import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Filter, X, SlidersHorizontal, Grid, List, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import ProfileCard from "../components/ProfileCard";
import SearchBar from "../components/SearchBar";
import BannerDisplay from "../components/BannerDisplay";
import SEO from "../components/SEO";
import axios from "axios";
import { API } from "../App";

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  // Filter state
  const [filters, setFilters] = useState({
    query: searchParams.get("query") || "",
    skills: searchParams.get("skills")?.split(",").filter(Boolean) || [],
    location: searchParams.get("location") || "",
    experience_level: searchParams.get("experience") || "",
    availability: searchParams.get("availability") || "",
    rate_min: searchParams.get("rate_min") || "",
    rate_max: searchParams.get("rate_max") || "",
    is_verified: searchParams.get("verified") === "true",
    is_featured: searchParams.get("featured") === "true",
  });

  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "created_at");

  useEffect(() => {
    fetchFiltersData();
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [filters, sortBy, currentPage]);

  const fetchFiltersData = async () => {
    try {
      const [categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
      ]);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.query) params.set("query", filters.query);
      if (filters.skills.length) params.set("skills", filters.skills.join(","));
      if (filters.location) params.set("location", filters.location);
      if (filters.experience_level) params.set("experience_level", filters.experience_level);
      if (filters.availability) params.set("availability", filters.availability);
      if (filters.rate_min) params.set("rate_min", filters.rate_min);
      if (filters.rate_max) params.set("rate_max", filters.rate_max);
      if (filters.is_verified) params.set("is_verified", "true");
      if (filters.is_featured) params.set("is_featured", "true");
      params.set("sort_by", sortBy);
      params.set("page", currentPage.toString());
      params.set("limit", "20");

      const response = await axios.get(`${API}/profiles?${params.toString()}`);
      setProfiles(response.data.profiles);
      setTotalProfiles(response.data.total);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const toggleSkill = (skill) => {
    setFilters((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      query: "",
      skills: [],
      location: "",
      experience_level: "",
      availability: "",
      rate_min: "",
      rate_max: "",
      is_verified: false,
      is_featured: false,
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = 
    filters.query ||
    filters.skills.length > 0 ||
    filters.location ||
    filters.experience_level ||
    filters.availability ||
    filters.rate_min ||
    filters.rate_max ||
    filters.is_verified ||
    filters.is_featured;

  const FilterSidebar = ({ className = "" }) => (
    <div className={`space-y-6 ${className}`}>
      {/* Skills/Categories */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Skills & Services
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {categories.map((cat) => (
            <label
              key={cat.category_id}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <Checkbox
                checked={filters.skills.includes(cat.name)}
                onCheckedChange={() => toggleSkill(cat.name)}
                className="border-zinc-600 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
              />
              <span className="text-sm text-zinc-400 group-hover:text-white transition-colors">
                {cat.name}
              </span>
              <span className="ml-auto text-xs text-zinc-600">
                {cat.profile_count || 0}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Location */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Location
        </h4>
        <Select
          value={filters.location || "all"}
          onValueChange={(value) => updateFilter("location", value === "all" ? "" : value)}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-300">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.location_id} value={loc.name}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Experience Level */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Experience Level
        </h4>
        <Select
          value={filters.experience_level || "all"}
          onValueChange={(value) => updateFilter("experience_level", value === "all" ? "" : value)}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-300">
            <SelectValue placeholder="Any Level" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">Any Level</SelectItem>
            <SelectItem value="entry">Entry Level</SelectItem>
            <SelectItem value="mid">Mid Level</SelectItem>
            <SelectItem value="senior">Senior</SelectItem>
            <SelectItem value="expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Availability */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Availability
        </h4>
        <Select
          value={filters.availability || "all"}
          onValueChange={(value) => updateFilter("availability", value === "all" ? "" : value)}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-300">
            <SelectValue placeholder="Any Availability" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">Any Availability</SelectItem>
            <SelectItem value="available">Available Now</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="unavailable">Unavailable</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Day Rate Range */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Day Rate (ZAR)
        </h4>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            placeholder="Min"
            value={filters.rate_min}
            onChange={(e) => updateFilter("rate_min", e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-zinc-300"
          />
          <span className="text-zinc-600">-</span>
          <Input
            type="number"
            placeholder="Max"
            value={filters.rate_max}
            onChange={(e) => updateFilter("rate_max", e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-zinc-300"
          />
        </div>
      </div>

      {/* Quick Filters */}
      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
          Quick Filters
        </h4>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer group">
            <Checkbox
              checked={filters.is_verified}
              onCheckedChange={(checked) => updateFilter("is_verified", checked)}
              className="border-zinc-600 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
            />
            <span className="text-sm text-zinc-400 group-hover:text-white transition-colors">
              Verified Only
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <Checkbox
              checked={filters.is_featured}
              onCheckedChange={(checked) => updateFilter("is_featured", checked)}
              className="border-zinc-600 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
            />
            <span className="text-sm text-zinc-400 group-hover:text-white transition-colors">
              Featured Only
            </span>
          </label>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full border-zinc-700 text-zinc-400 hover:text-white"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="browse-page">
      <SEO 
        url="/browse"
        title="Find Creative Professionals"
        description="Browse and hire videographers, cinematographers, editors, and creative professionals in Cape Town. Filter by skill, location, experience, and availability."
        keywords={["hire videographer", "find cinematographer", "cape town film crew", "creative professionals"]}
      />
      
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-title uppercase mb-4">
            FIND <span className="text-orange-500">CREATIVES</span>
          </h1>
          <div className="max-w-2xl">
            <SearchBar
              placeholder="Search by name, skill, or keyword..."
              onSearch={(query) => updateFilter("query", query)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Banner Ad - Browse placement */}
        <div className="mb-6">
          <BannerDisplay placement="browse" />
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="filter-sidebar pr-4">
              <FilterSidebar />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Active Filters & Sort */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                  <SheetTrigger asChild className="lg:hidden">
                    <Button variant="outline" className="border-zinc-700">
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      Filters
                      {hasActiveFilters && (
                        <span className="ml-2 w-5 h-5 bg-orange-600 rounded-full text-xs flex items-center justify-center">
                          {filters.skills.length + (filters.location ? 1 : 0) + (filters.experience_level ? 1 : 0)}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 bg-zinc-950 border-zinc-800">
                    <SheetHeader>
                      <SheetTitle className="text-white">Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterSidebar />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Results Count */}
                <p className="text-zinc-500 text-sm">
                  <span className="text-white font-medium">{totalProfiles}</span> profiles found
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-zinc-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="created_at">Newest</SelectItem>
                    <SelectItem value="view_count">Most Viewed</SelectItem>
                    <SelectItem value="contact_count">Most Contacted</SelectItem>
                    <SelectItem value="day_rate_min">Price: Low to High</SelectItem>
                  </SelectContent>
                </Select>

                {/* View Mode */}
                <div className="hidden md:flex items-center border border-zinc-700 rounded">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 ${viewMode === "grid" ? "bg-zinc-800 text-white" : "text-zinc-500"}`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 ${viewMode === "list" ? "bg-zinc-800 text-white" : "text-zinc-500"}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filter Tags */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-6">
                {filters.query && (
                  <Badge
                    variant="secondary"
                    className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700 cursor-pointer"
                    onClick={() => updateFilter("query", "")}
                  >
                    Search: {filters.query}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                )}
                {filters.skills.map((skill) => (
                  <Badge
                    key={skill}
                    variant="secondary"
                    className="bg-orange-900/50 text-orange-400 hover:bg-orange-900 cursor-pointer"
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
                {filters.location && (
                  <Badge
                    variant="secondary"
                    className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700 cursor-pointer"
                    onClick={() => updateFilter("location", "")}
                  >
                    {filters.location}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                )}
              </div>
            )}

            {/* Profiles Grid */}
            {loading ? (
              <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="aspect-cinematic bg-zinc-900 skeleton" />
                ))}
              </div>
            ) : profiles.length > 0 ? (
              <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                {profiles.map((profile) => (
                  <ProfileCard key={profile.profile_id} profile={profile} variant={viewMode} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Filter className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white mb-2">No profiles found</h3>
                <p className="text-zinc-500 mb-4">Try adjusting your filters or search terms</p>
                <Button
                  onClick={clearFilters}
                  className="bg-orange-600 hover:bg-orange-500"
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/* Pagination */}
            {totalProfiles > 20 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="border-zinc-700"
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-zinc-400">
                  Page {currentPage} of {Math.ceil(totalProfiles / 20)}
                </span>
                <Button
                  variant="outline"
                  disabled={currentPage >= Math.ceil(totalProfiles / 20)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="border-zinc-700"
                >
                  Next
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
